<template>
  <MinimalLayout
    :loading="step === 'loading'"
    :title="
      step === 'phoneNumber' || step === 'phoneNumberVerification' || step === 'treezorSCA'
        ? t('mfa.title')
        : t('mfa.verifyEmail')
    "
  >
    <template v-if="step === 'emailVerification'">
      <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
        <span>{{ error }}</span>
      </v-alert>

      <div v-else class="flex flex-col items-center justify-center mb-4">
        <div class="text-base text-center flex items-center justify-center">
          <v-icon icon="mdi-email-fast" size="24" class="mr-2"></v-icon>
          {{ t("mfa.emailVerificationSent", { email }) }}
        </div>
        <v-progress-circular
          indeterminate
          color="primary"
          size="32"
          width="3"
          class="mt-6"
        ></v-progress-circular>
      </div>

      <v-btn class="mt-6" block size="small" variant="text" @click="handleBackToLogin">
        {{ t("mfa.backToLogin") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'phoneNumber'">
      <div class="text-sm text-center mb-4">
        {{ t("mfa.phoneNumberDescription") }}
      </div>

      <v-form ref="phoneNumberForm" :disabled="loading" @submit.prevent="submitPhoneNumber">
        <v-text-field
          v-model.trim="phoneNumber"
          :rules="[required, validPhoneNumber]"
          clearable
          :label="t('mfa.phoneNumber')"
          :placeholder="t('mfa.phoneNumberPlaceholder')"
          persistent-placeholder
          class="mb-2"
          variant="outlined"
          autocomplete="tel"
        ></v-text-field>

        <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
          <span>{{ error }}</span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
        >
          {{ t("mfa.submitPhoneNumber") }}
        </v-btn>
      </v-form>

      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="handleBackToLogin"
      >
        {{ t("mfa.backToLogin") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'phoneNumberVerification'">
      <div class="text-sm text-center mb-4">
        {{ t("mfa.verificationCodeSentTo", { phoneNumber }) }}
      </div>
      <v-form
        ref="phoneNumberVerificationForm"
        :disabled="loading"
        @submit.prevent="submitPhoneNumberVerification"
      >
        <v-text-field
          v-model.trim="verificationCode"
          :rules="[required]"
          autocomplete="one-time-code"
          clearable
          :label="t('mfa.phoneNumberVerificationCode')"
          class="mb-2"
          variant="outlined"
          maxlength="6"
          inputmode="numeric"
        ></v-text-field>

        <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
          <span>{{ error }}</span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
        >
          {{ t("mfa.submitPhoneNumberVerification") }}
        </v-btn>
      </v-form>

      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="step = 'phoneNumber'"
      >
        {{ t("mfa.changePhoneNumber") }}
      </v-btn>
      <v-btn
        class="mt-2"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="handleBackToLogin"
      >
        {{ t("mfa.backToLogin") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'treezorSCA'">
      <div class="text-sm text-center mb-4">
        {{ t("mfa.treezorScaDescription") }}
      </div>

      <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
        <span>{{ error }}</span>
      </v-alert>

      <v-btn
        :disabled="loading"
        :loading="loading"
        block
        color="primary"
        size="large"
        variant="flat"
        @click="submitTreezorSCAEnrollment"
      >
        {{ t("mfa.treezorScaEnroll") }}
      </v-btn>

      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="handleBackToLogin"
      >
        {{ t("mfa.backToLogin") }}
      </v-btn>
    </template>
  </MinimalLayout>
  <div ref="recaptchaContainer"></div>
</template>

<script lang="ts" setup>
import MinimalLayout from "@/components/MinimalLayout.vue";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useRedirect } from "@/router/redirect";
import { useToast, useUserStore } from "@/store";
import { useFirebaseErrorMessage } from "@/utils/errors";
import { verifyPhoneNumber } from "@/utils/recaptcha";
import { startAuthentication, startRegistration } from "@/utils/webAuthn";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import {
  AuthErrorCodes,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  signInWithCustomToken,
} from "firebase/auth";
import { nextTick, onUnmounted, ref, watch } from "vue";
import { useFirebaseAuth } from "vuefire";
import type { VForm } from "vuetify/components";

const { t } = useI18n();
const { currentLanguage } = useLanguage();
const user = useUserStore();
const auth = useFirebaseAuth();
const { getFirebaseErrorMessage } = useFirebaseErrorMessage();
const { required, validPhoneNumber } = useRules();
const { showSuccessToast, showWarningToast } = useToast();
const { redirectToHome } = useRedirect();

const handleBackToLogin = async () => {
  await auth?.signOut();
};

const email = ref<string>("");
const phoneNumber = ref("");
const verificationCode = ref<string>("");
const loading = ref(false);
const phoneNumberForm = ref<VForm>();
const phoneNumberVerificationForm = ref<VForm>();
const error = ref<null | string>(null);
const step = ref<
  "loading" | "emailVerification" | "phoneNumber" | "phoneNumberVerification" | "treezorSCA"
>("loading");
const verificationId = ref<string>("");
const recaptchaContainer = ref<HTMLElement>();
const checkEmailIsVerifiedCounter = ref<number>(0);

const goToNextEnrollmentStep = () => {
  const authenticatedUser = user.authenticatedUser;
  if (!authenticatedUser) throw new Error("User is not authenticated");

  if (!authenticatedUser.authClaims.isMfaEnrolled) {
    if (user.isKind("user")) {
      console.log("Moving to Treezor SCA enrollment");
      loading.value = false;
      step.value = "treezorSCA";
      return;
    }

    console.log("Moving to phone number enrollment");
    loading.value = false;
    step.value = "phoneNumber";
    return;
  }

  console.log("MFA enrollment complete, redirecting away");
  showSuccessToast(t("mfa.enrollmentSuccessful"));
  void redirectToHome();
};

const startCheckEmailIsVerified = async () => {
  if (checkEmailIsVerifiedCounter.value++ > 30) {
    console.log("Email verification check timed out after 5 minutes, signing out user");
    showWarningToast(t("mfa.emailVerificationTimeout"));
    await handleBackToLogin();
    return;
  }

  if (!user.authenticatedUser) throw new Error("User is not authenticated");

  console.log("Reloading user to check email verification status");
  await user.authenticatedUser.firebaseUser.reload();

  if (user.authenticatedUser.firebaseUser.emailVerified) {
    console.log("Email verified, stopping check");
    return;
  }

  console.log("Email not verified yet, checking again in 10 seconds");
  await new Promise((resolve) => setTimeout(resolve, 10_000));
  return startCheckEmailIsVerified();
};

const submitEmailVerification = async () => {
  error.value = null;
  try {
    loading.value = true;

    if (!user.authenticatedUser) throw new Error("User is not authenticated");

    console.log("Sending email verification to user");
    await call("sendUserVerificationEmail")({
      locale: currentLanguage.value.code,
    });

    checkEmailIsVerifiedCounter.value = 0;
    await startCheckEmailIsVerified();

    loading.value = false;

    if (user.authenticatedUser.firebaseUser.emailVerified) {
      goToNextEnrollmentStep();
    }
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Mfa submitEmailVerification function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("mfa.error");
    }
  }
};

watch(
  user,
  async (user) => {
    if (step.value === "treezorSCA" && user.authenticatedUser) {
      goToNextEnrollmentStep();
      return;
    }

    if (step.value !== "loading") return;

    if (!user.authenticatedUser || !user.authenticatedUser.firebaseUser.email) {
      console.log("User not properly authenticated, signing out and redirecting to login");
      await handleBackToLogin();
      return;
    }

    if (user.authenticatedUser.authClaims.isMfaEnrolled) {
      goToNextEnrollmentStep();
      return;
    }

    const authTimeMs = new Date(user.authenticatedUser.idToken.authTime).getTime();
    const fiveMinutesInMs = 5 * 60 * 1000;
    if (Date.now() > authTimeMs + fiveMinutesInMs) {
      console.log("User session is not recent enough for MFA, signing out");
      showWarningToast(t("errors.sessionExpired"));
      await handleBackToLogin();
      return;
    }
    console.log("User session still recent", new Date(authTimeMs).toISOString());

    email.value = user.authenticatedUser.firebaseUser.email;

    if (user.authenticatedUser.firebaseUser.emailVerified) {
      goToNextEnrollmentStep();
      return;
    }

    console.log("Email not verified, moving to email verification step");
    step.value = "emailVerification";
    await submitEmailVerification();
  },
  { immediate: true },
);

const submitTreezorSCAEnrollment = async () => {
  error.value = null;
  try {
    if (!auth) throw new Error("Auth is not defined");

    loading.value = true;
    console.log("Starting Treezor SCA enrollment");

    const enrollmentSca = await call("enrollTreezorSCAPayload")({});
    const webAuthnRegistrationResponse = await startRegistration({
      optionsJSON: enrollmentSca.webAuthnRegistrationOptions,
    });

    await call("enrollTreezorSCA")({ webAuthnRegistrationResponse: webAuthnRegistrationResponse });

    // Complete SCA sign-in so claims include treezorSCAExpiresAt (marks MFA as enrolled)
    console.log("Signing in with newly enrolled Treezor SCA");

    const { webAuthnAuthenticationOptions } = await call("signInWithTreezorSCAPayload")({});
    const webAuthnAuthenticationResponse = await startAuthentication({
      optionsJSON: webAuthnAuthenticationOptions,
    });

    const { token } = await call("signInWithTreezorSCA")({ webAuthnAuthenticationResponse });

    await signInWithCustomToken(auth, token);
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Mfa submitTreezorSCAEnrollment function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("mfa.error");
    }
  }
};

const submitPhoneNumber = async () => {
  error.value = null;
  try {
    const phoneNumberVerificationForm = await phoneNumberForm.value?.validate();
    if (!phoneNumberVerificationForm?.valid) return;

    if (!auth) throw new Error("Auth is not defined");
    if (!recaptchaContainer.value) throw new Error("reCAPTCHA container is not mounted");
    if (!user.authenticatedUser) throw new Error("User is not authenticated");

    loading.value = true;

    console.log("Starting MFA enrollment process");
    const multiFactorSession = await multiFactor(user.authenticatedUser.firebaseUser).getSession();
    const phoneInfoOptions = {
      phoneNumber: phoneNumber.value,
      session: multiFactorSession,
    };

    console.log("Sending verification code to phone");
    verificationId.value = await verifyPhoneNumber(
      auth,
      recaptchaContainer.value,
      phoneInfoOptions,
    );

    step.value = "phoneNumberVerification";
    loading.value = false;
  } catch (cause) {
    loading.value = false;

    if (
      cause instanceof FirebaseError &&
      cause.code === AuthErrorCodes.CREDENTIAL_TOO_OLD_LOGIN_AGAIN
    ) {
      showWarningToast(t("errors.sessionExpired"));
      await handleBackToLogin();
      return;
    }

    const wrappedError = new Error("Error in Mfa submitPhoneNumber function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("mfa.error");
    }
  }
};

const submitPhoneNumberVerification = async () => {
  error.value = null;
  try {
    const phoneNumberVerificationFormResult = await phoneNumberVerificationForm.value?.validate();
    if (!phoneNumberVerificationFormResult?.valid) return;

    if (!user.authenticatedUser) throw new Error("User is not authenticated");

    loading.value = true;

    console.log("Verifying code entered by user");
    const credential = PhoneAuthProvider.credential(verificationId.value, verificationCode.value);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    await multiFactor(user.authenticatedUser.firebaseUser).enroll(multiFactorAssertion);

    console.log("Phone enrolled, signing out so beforeUserSignedIn sets the MFA claims");
    showSuccessToast(t("mfa.phoneEnrollmentSuccessful"));
    await handleBackToLogin();
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Mfa submitPhoneNumberVerification function", {
      cause,
    });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("mfa.error");
    }
  }
};

watch(step, () => {
  phoneNumberForm.value?.resetValidation();
  phoneNumberVerificationForm.value?.resetValidation();
  error.value = null;
  verificationCode.value = "";
});

watch(verificationCode, async (code) => {
  if (loading.value) return;
  if (/^\d{6}$/.test(code)) {
    await nextTick();
    void submitPhoneNumberVerification();
  }
});

onUnmounted(() => {
  checkEmailIsVerifiedCounter.value = 10000000;
});
</script>

<style lang="css">
.grecaptcha-badge {
  visibility: hidden;
}
</style>
